import { Context, createContext } from 'react'

/**
 * A globals registry for managing React Contexts created in adapter packages.
 *
 * Since adapters (e.g. `@skedulo/horizon-react`) are rolled into multiple
 * component bundles, any Contexts they create will be duplicated and scoped to
 * the resulting module. This is fine in some cases, but if the Context is
 * intended to be globally available, it should be created in the Core registry
 * instead.
 *
 * Currently this is only used within Horizon Framework packages which interface
 * with Horizon Core, but we could consider exposing an extension of this
 * mechanism to component authors for sharing Contexts across multiple bundles.
 * That might require scoping Contexts by package name and version, or similar.
 */
export class ContextRegistry {
  #contexts = new Map<string, Context<unknown>>()

  constructor() {
    this.create = this.create.bind(this)
  }

  /**
   * Creates a new React Context if it doesn't exist, otherwise returns the
   * existing one.
   *
   * Has the same signature as `React.createContext` but with a name to identify
   * it in the registry.
   *
   * @see {React.createContext}
   */
  create<ContextValue>(
    contextName: string,
    initialValue: ContextValue,
  ): Context<ContextValue> {
    if (!this.#contexts.has(contextName)) {
      this.#contexts.set(contextName, createContext(initialValue as unknown))
    }

    return this.#contexts.get(contextName) as Context<ContextValue>
  }
}

export const contexts = new ContextRegistry()

export const createGlobalContext = contexts.create
